import { Badge, Flex, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ActionButton, ActionFab, CopyButton } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EMPTY_CELL_VALUE, EmptyCell } from '../../../components/EmptyCell'
import { FILTER_CONTROL_SIZE, FilterBar, FilterField, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { formatCurrency, formatDate, useAppToast, useHasRole, WEBSITE_URL } from '../../../helpers'
import { EventService } from '../../../services/Event/EventService'
import { SelectEvent } from '../../../services/Event/types'
import EventCouponService from '../../../services/EventCoupons/EventCouponService'
import { CouponCommissionTypeFilter, IEventCoupon, IEventCouponFilter } from '../../../services/EventCoupons/interfaces'
import {
  commissionBeneficiaryName,
  commissionTypeFilterOptions,
  commissionTypeLabels,
  discountTypeLabels,
  freeCouponCategoryLabels
} from './couponLabels'
import { EventCouponFormModal } from './EventCouponFormModal'

function formatDiscount(coupon: IEventCoupon): string {
  if (coupon.discount_type === 'percentage') return `${coupon.discount_value ?? 0}%`
  if (coupon.discount_type === 'fixed') {
    return formatCurrency(coupon.discount_value ?? 0)
  }
  return EMPTY_CELL_VALUE
}

/**
 * "3% · 5% esgotado" — the sold-out override only shows when there is one.
 *
 * A coupon with no rate reads "—", never "0%": the form requires a rate whenever there is a
 * commission, so a missing one means unknown, not free.
 */
function formatCommissionRate(coupon: IEventCoupon): string {
  if (coupon.commission_rate == null) return EMPTY_CELL_VALUE

  const rate = `${coupon.commission_rate}%`

  return coupon.commission_rate_sold_out !== null ? `${rate} · ${coupon.commission_rate_sold_out}% esgotado` : rate
}

/**
 * Um cupom ou comissiona alguém, ou é gratuito e tem uma categoria — nunca os dois.
 */
function CommissionCell({ coupon }: { coupon: IEventCoupon }) {
  if (coupon.free_coupon_category) {
    return <Badge colorScheme="green">{freeCouponCategoryLabels[coupon.free_coupon_category]}</Badge>
  }

  if (!coupon.commission_type) return <EmptyCell />

  return (
    <Flex direction="column" gap={0.5} align="flex-start">
      <Badge colorScheme="blue">{commissionTypeLabels[coupon.commission_type]}</Badge>
      <EmptyCell variant="caption" value={commissionBeneficiaryName(coupon)} />
      <Text variant="caption">{formatCommissionRate(coupon)}</Text>
    </Flex>
  )
}

/** `page`/`page_size` ficam de fora: são paginação, não filtro. */
const INITIAL_FILTER: Omit<IEventCouponFilter, 'page' | 'page_size'> = {
  search: '',
  applicant_name: '',
  commission_type: '',
  is_active: '',
  event_id: '',
  ticket_id: ''
}

export default function EventCouponsPage() {
  const modalState = useDisclosure()
  const toast = useAppToast()
  const [searchParams] = useSearchParams()
  const urlFilterApplied = useRef(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [editingCoupon, setEditingCoupon] = useState<IEventCoupon | null>(null)
  const [deletingId, setDeletingId] = useState(0)
  const hasRole = useHasRole()
  const service = useMemo(() => new EventCouponService(), [])
  const eventService = useMemo(() => new EventService(), [])
  const [events, setEvents] = useState<SelectEvent[]>([])
  const [applicantOptions, setApplicantOptions] = useState<{ value: string; label: string }[]>([])
  const [coupons, setCoupons] = useState<IEventCoupon[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [totalPages, setTotalPages] = useState(1)
  const [filter, setFilter] = useState<IEventCouponFilter>({
    ...INITIAL_FILTER,
    page: 1,
    page_size: DEFAULT_PAGE_SIZE
  })
  const isDirty =
    filter.search !== '' ||
    filter.applicant_name !== '' ||
    filter.commission_type !== '' ||
    filter.is_active !== '' ||
    filter.event_id !== ''

  // Trocar um filtro volta para a página 1: filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia.
  const updateFilter = (patch: Partial<IEventCouponFilter>) => setFilter((f) => ({ ...f, ...patch, page: 1 }))

  const numericEventId = typeof filter.event_id === 'number' ? filter.event_id : null
  const selectedEvent = events.find((e) => e.id === numericEventId)

  const permissions = {
    view: hasRole('view_coupons'),
    add: hasRole('add_coupons'),
    edit: hasRole('edit_coupons'),
    delete: hasRole('delete_coupons'),
    toggle: hasRole('toggle_coupon_status')
  }

  useEffect(() => {
    eventService
      .listForSelect()
      .then(setEvents)
      .catch(() => toast.error('Erro ao carregar eventos'))
  }, [eventService, toast])

  // Applicant names are only relevant when a single (real) event is selected.
  useEffect(() => {
    if (numericEventId === null) {
      setApplicantOptions([])
      setFilter((f) => (f.applicant_name ? { ...f, applicant_name: '', page: 1 } : f))
      return
    }
    service
      .listApplicants(numericEventId)
      .then((names) => {
        setApplicantOptions([{ value: '', label: 'Todos os alunos' }, ...names.map((n) => ({ value: n, label: n }))])
      })
      .catch(() => undefined)
  }, [numericEventId, service])

  // Apply URL-based event filter on component load (?event=slug)
  useEffect(() => {
    if (events.length === 0 || urlFilterApplied.current) {
      return
    }

    const eventSlug = searchParams.get('event')
    if (!eventSlug) {
      return
    }

    const matchedEvent = events.find((event) => event.slug.toLowerCase() === eventSlug.toLowerCase())

    if (matchedEvent) {
      setFilter((f) => ({ ...f, event_id: matchedEvent.id, page: 1 }))
      urlFilterApplied.current = true
    }
  }, [events, searchParams])

  const fetchCoupons = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const response = await service.list(filter)
      setCoupons(response.data)
      setTotalPages(Math.ceil(response.total / filter.page_size) || 1)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os cupons', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCoupons()
  }, [filter])

  async function handleDelete() {
    try {
      await service.delete(deletingId)
      toast.success('Cupom excluído com sucesso!')
      fetchCoupons()
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } }
      const message = axiosError?.response?.data?.error || 'Erro ao excluir cupom'
      toast.error(message, err)
    } finally {
      setDeletingId(0)
    }
  }

  async function handleToggle(coupon: IEventCoupon) {
    try {
      await service.toggleStatus(coupon.id)
      fetchCoupons()
    } catch {
      toast.error('Erro ao alterar status do cupom')
    }
  }

  return (
    <>
      <PageTitle title="Cupons de Desconto" description="Cupons emitidos para os eventos da ABRA" />

      <FilterBar onClear={() => updateFilter(INITIAL_FILTER)} isDirty={isDirty}>
        <FilterField label="Evento" maxW="260px">
          <Flex gap={2} align="center">
            <FormSelect
              size={FILTER_CONTROL_SIZE}
              value={filter.event_id}
              onChange={(value) => {
                const val = value
                updateFilter({ event_id: val === '' || val === 'global' ? (val as '' | 'global') : Number(val) })
              }}
              options={[
                { value: 'global', label: '🌐 Cupons globais (todos os eventos)' },
                ...events.map((ev) => ({ value: ev.id, label: ev.name }))
              ]}
              placeholder="Todos os cupons"
            />
            <CopyButton
              value={selectedEvent ? `${WEBSITE_URL}/eventos/${selectedEvent.slug}` : ''}
              entity="link do evento"
              description={selectedEvent ? `${WEBSITE_URL}/eventos/${selectedEvent.slug}` : undefined}
              label={selectedEvent ? 'Copiar link do evento' : 'Selecione um evento'}
              size={FILTER_CONTROL_SIZE}
              mr={0}
              colorScheme="magenta"
              isDisabled={!selectedEvent}
            />
          </Flex>
        </FilterField>
        <FilterSearch
          label="Buscar"
          maxW="290px"
          value={filter.search}
          onChange={(search) => updateFilter({ search })}
          placeholder="Código ou nome do comissionado"
        />
        <FilterField label="Nome do Aluno" maxW="250px">
          <SearchableSelect
            options={applicantOptions}
            value={filter.applicant_name}
            onChange={(applicant_name) => updateFilter({ applicant_name })}
            placeholder="Todos os alunos"
            size="sm"
          />
        </FilterField>
        <FilterSelect
          label="Tipo de comissionamento"
          maxW="220px"
          value={filter.commission_type}
          onChange={(val) => updateFilter({ commission_type: val as CouponCommissionTypeFilter })}
          options={commissionTypeFilterOptions}
        />
        <FilterSelect
          label="Status"
          maxW="180px"
          value={filter.is_active === '' ? '' : filter.is_active ? '1' : '0'}
          onChange={(val) => updateFilter({ is_active: val === '' ? '' : val === '1' })}
          options={[
            { value: '1', label: 'Ativo' },
            { value: '0', label: 'Inativo' }
          ]}
        />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Código</Th>
              <Th>Evento</Th>
              <Th>Nome do Aluno</Th>
              <Th>Ingresso</Th>
              <Th>Tipo</Th>
              <Th>Desconto</Th>
              <Th>Comissionamento</Th>
              <Th>Usos</Th>
              <Th>Expirado</Th>
              <Th>Validade</Th>
              <Th>Origem</Th>
              <Th>Ativo</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={14} />
            ) : loadError ? (
              <TableErrorRow colSpan={14} entity="cupons" onRetry={fetchCoupons} />
            ) : coupons.length === 0 ? (
              <TableEmptyRow colSpan={14} entity="cupom" />
            ) : (
              coupons.map((coupon) => (
                <Tr key={coupon.id}>
                  <Td>{coupon.id}</Td>
                  <Td fontWeight="bold">{coupon.code}</Td>
                  <Td>
                    {coupon.event_id === null ? (
                      <Badge colorScheme="purple">Todos os eventos</Badge>
                    ) : (
                      <EmptyCell value={coupon.event?.name} />
                    )}
                  </Td>
                  <Td>
                    <EmptyCell value={coupon.contest_application?.applicant.name} />
                  </Td>
                  <Td>{coupon.ticket?.title ?? 'Todos os ingressos'}</Td>
                  <Td>{discountTypeLabels[coupon.discount_type] ?? coupon.discount_type}</Td>
                  <Td>{formatDiscount(coupon)}</Td>
                  <Td>
                    <CommissionCell coupon={coupon} />
                  </Td>
                  <Td>
                    {coupon.used_count}/{coupon.max_uses ?? '∞'}
                  </Td>
                  <Td>{coupon.is_expired ? <Badge colorScheme="red">Expirado</Badge> : <EmptyCell />}</Td>
                  <Td>{coupon.expires_at ? formatDate(coupon.expires_at) : 'Sem validade'}</Td>
                  <Td>
                    {coupon.contest_application ? (
                      <Tooltip label={`Gerado por inscrito ${coupon.contest_application.applicant.name}`}>
                        <Badge colorScheme="orange" cursor="default">
                          Sistema
                        </Badge>
                      </Tooltip>
                    ) : (
                      <EmptyCell value={coupon.creator?.name} />
                    )}
                  </Td>
                  <Td>
                    {coupon.is_active ? (
                      <Badge colorScheme="green">Ativo</Badge>
                    ) : (
                      <Badge colorScheme="red">Inativo</Badge>
                    )}
                  </Td>
                  <Td>
                    {permissions.toggle && (
                      <ActionButton
                        action={coupon.is_active ? 'lock' : 'unlock'}
                        label={coupon.is_active ? 'Desativar' : 'Ativar'}
                        colorScheme={coupon.is_active ? 'orange' : 'green'}
                        icon={<Icon name="lni lni-power-switch" />}
                        onClick={() => handleToggle(coupon)}
                      />
                    )}
                    {permissions.edit && (
                      <ActionButton
                        action="edit"
                        label="Editar cupom"
                        onClick={() => {
                          setModalMode('edit')
                          setEditingCoupon(coupon)
                          modalState.onOpen()
                        }}
                      />
                    )}
                    {permissions.delete && (
                      <ActionButton
                        action="delete"
                        label="Excluir cupom"
                        onClick={() => {
                          setDeletingId(coupon.id)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={filter.page}
        totalPages={totalPages}
        changePage={(page: number) => setFilter((f) => ({ ...f, page }))}
        pageSize={filter.page_size}
        onPageSizeChange={(size) => {
          setFilter({ ...filter, page_size: size, page: 1 })
        }}
      />

      {permissions.add && (
        <ActionFab
          label="Adicionar cupom"
          onClick={() => {
            setModalMode('create')
            setEditingCoupon(null)
            modalState.onOpen()
          }}
        />
      )}

      <EventCouponFormModal
        isOpen={modalState.isOpen}
        onClose={modalState.onClose}
        coupon={editingCoupon}
        mode={modalMode}
        onSuccess={fetchCoupons}
      />

      <ConfirmDialog
        isOpen={deletingId > 0}
        onClose={() => setDeletingId(0)}
        onConfirm={handleDelete}
        action="delete"
        entity="cupom"
      />
    </>
  )
}
